import React, { useEffect, useState } from 'react'
import { SelectField, InputField } from '../../shared/FormFields'
import { RadioGroup, RadioGroupItem } from '../../../lib/ui/radio-group'
import { Form } from '../../../lib/ui/form'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '../../../lib/ui/button'
import AylaResponseScrollArea from '../../ShortCourses/AylaResponseScrollArea'
import { useAiResponseStream } from '../useAiResponseStream'
import AylaRatingFeedback from '../../shared/AylaRatingFeedback'
import { TranslationProvider } from '../../shared/TranslationProvider'
import translations from '../locales.json'
import { useTranslation } from '../../../hooks/useTranslation'
import SavedStatusIcon from '~/components/shared/SavedStatusIcon'
import AylaLoading from '../../shared/AylaLoading'
import AylaTimeoutError, { aylaPost } from '~/components/shared/AylaTimeout'

const AylaIndustryResearch = (props) => {
  const { t } = useTranslation()
  const { industries } = props
  const [lastIndustry, setLastIndustry] = useState('')
  const [interest, setInterest] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [displayedText, setDisplayedText] = useState('')
  const [index, setIndex] = useState(0)
  const [loadingStatus, setLoadingStatus] = useState(null)
  const [submitFailed, setSubmitFailed] = useState(false)
  const showCursor = submitted && displayedText.length <= 0
  const {
    aiResponse,
    researchId,
    setResearchId,
    aiStreamComplete,
    setAiStreamComplete,
    aiFailed,
    resetAiResponse,
  } = useAiResponseStream()
  const showAylaError = aiFailed || submitFailed

  const schema = z.object({
    industry: z.string().optional(),
    years: z.string().nonempty().optional(),
  })

  const form = useForm({
    resolver: zodResolver(schema),
  })

  const handleSubmitButtonClick = async () => {
    setSubmitted(true)
    setLoadingStatus('loading')
    setSubmitFailed(false)
    resetAiResponse()
    setDisplayedText('')
    setIndex(0)
    setLastIndustry(industry)

    const response = await aylaPost(
      '/research_learning/ayla/create_industry_research',
      {
        ai_industry_research: {
          industry: industry || '',
          interest: interest || '',
          years: years || '',
        },
      },
    ).catch(() => null)
    if (!response) {
      setLoadingStatus(null)
      setSubmitFailed(true)
      return
    }
    const {
      data: { industry_research_id },
    } = response
    setResearchId(industry_research_id)
    setLoadingStatus('complete')
  }

  const transformAylaResponse = (response) => {
    const urlRegex = /((https?:\/\/|www\.)[^\s]+)/g
    return response.replace(urlRegex, (url) => {
      const formattedUrl = url.startsWith('http') ? url : `https://${url}`
      return `<a href="${formattedUrl}" target="_blank" rel="noopener noreferrer">${formattedUrl}</a>`
    })
  }

  useEffect(() => {
    if (aiStreamComplete) {
      setLoadingStatus('complete')
    }
  }, [aiStreamComplete])

  const industry = form.watch('industry')
  const years = form.watch('years')
  const starDescriptions = t('star_ratings', {
    defaultValue: [
      'Missed the Mark',
      'Needs Improvement',
      'Pretty Good',
      'Really Helpful',
      'Exactly What I Needed',
    ],
  })

  return (
    <div className="m-4 flex flex-col items-baseline gap-2 md:m-8">
      <h1>{t('title')}</h1>
      <p className="w-3/4">{t('description')}</p>
      <p>{t('tell_ayla')}</p>
      <Form {...form}>
        <div className="flex items-center gap-4">
          <label htmlFor="industry" className="!m-0">
            {t('want_to_learn')}
          </label>
          <SelectField
            name="industry"
            className="w-48"
            control={form.control}
            placeholder={t('select_one')}
            choices={industries.map((industry) => ({
              value: industry,
              label: industry,
            }))}
          />
          <label className="!m-0">{t('industry')}</label>
        </div>
        <label className="mt-4">{t('primarily_interested')}</label>
        <RadioGroup
          className="mt-2"
          onValueChange={(value) => setInterest(value)}
        >
          <label className="mb-2 !flex items-center">
            <RadioGroupItem value="pivot" className="mr-2" />
            {t('career_pivot')}
          </label>
          <label className="mb-2 !flex items-center">
            <RadioGroupItem value="trends" className="mr-2" />
            {t('industry_trends')}
          </label>
          <label className="mb-2 !flex items-center">
            <RadioGroupItem value="change" className="mr-2" />
            {t('change')}
          </label>
        </RadioGroup>
        {(interest == 'trends' || interest == 'change') && (
          <div className="flex items-center gap-4">
            <label htmlFor="industry" className="!m-0">
              {t('worked_in_industry')}
            </label>
            <InputField
              name="years"
              className="w-24"
              control={form.control}
              value={years}
            />
            <label className="!m-0">{t('years')}</label>
          </div>
        )}
      </Form>
      <div className="my-4">
        <Button
          disabled={!(industry && interest) || (submitted && !aiStreamComplete)}
          onClick={() => handleSubmitButtonClick()}
        >
          {t('lets_go_ayla')}
          <SavedStatusIcon loadingStatus={loadingStatus} />
        </Button>
      </div>
      {submitted && (
        <div className="subtitle">
          {t('what_found', { industry: lastIndustry })}
        </div>
      )}
      {showAylaError && (
        <AylaTimeoutError onRetry={() => handleSubmitButtonClick()} />
      )}
      {!showAylaError &&
        (loadingStatus === 'loading' ||
          (loadingStatus === 'complete' && aiResponse?.length <= 0 && (
            <div className="flex items-center justify-center">
              <AylaLoading />
            </div>
          )))}
      {!showAylaError &&
        loadingStatus === 'complete' &&
        aiResponse?.length > 0 && (
          <AylaResponseScrollArea
            showBorder={submitted}
            showCursor={showCursor}
            aylaResponse={aiResponse}
            typingComplete={aiStreamComplete}
            setTypingComplete={setAiStreamComplete}
            displayedText={
              aiStreamComplete
                ? transformAylaResponse(displayedText).replace(/\n/g, '<br>')
                : displayedText.replace(/\n/g, '<br>')
            }
            setDisplayedText={setDisplayedText}
            index={index}
            setIndex={setIndex}
            className="mb-4 h-64"
          ></AylaResponseScrollArea>
        )}
      {submitted && aiStreamComplete && (
        <div className="flex w-full flex-col gap-4">
          <a
            href={`industry-research.pdf?id=${researchId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-fit"
          >
            <Button>{t('download_report')}</Button>
          </a>
          <AylaRatingFeedback
            routes={{
              createFeedback: `/research_learning/ayla/feedback`,
            }}
            content={researchId}
            title={t('how_helpful')}
            subtitle={t('your_feedback')}
            starDescriptions={starDescriptions}
            documentType="Feedback::AylaIndustryResearch"
            className="sm:w-1/2"
          />
        </div>
      )}
    </div>
  )
}

const Index = (props) => {
  const { locale } = props

  return (
    <TranslationProvider
      translations={translations}
      locale={locale}
      scope="javascript.components.ResearchLearning.AylaIndustryResearch"
    >
      <AylaIndustryResearch {...props} />
    </TranslationProvider>
  )
}

export default Index
